/**
 * The fields that reach into another table.
 *
 * A link row points at a table; a count, a rollup and a lookup reach *through*
 * one of those links at a field on the other side. That is the whole of what
 * separates these four from every other field type, and it is why the type
 * picker could not offer them until there was somewhere to pick a table from.
 *
 * Pure. The pickers that read it are in `ui/forms/relations.tsx`.
 */
import type { Application, DatabaseApplication, Field, Table } from '@/api/types'
import { nameLiteral } from '../formula/names'

/**
 * The tables a link row field on this table may point at: its own siblings.
 *
 * A link row must stay inside one database -- the backend refuses across two,
 * because the relation is a real foreign key in one schema -- and the table's
 * own database is found by looking for the one that has it, rather than being
 * passed in. The applications cache already holds every table of every database
 * in the workspace, so asking which database a table is in is a question it can
 * answer, and a second source for it would be a second thing to keep current.
 *
 * The table itself is included: a link row may point at its own table, which is
 * how a hierarchy is written.
 */
export function tablesBeside(
  applications: readonly Application[] | undefined,
  tableId: number,
): Table[] {
  const database = (applications ?? [])
    .filter(
      (application): application is DatabaseApplication =>
        application.type === 'database',
    )
    .find((application) => application.tables.some((table) => table.id === tableId))

  return database?.tables ?? []
}

/** The link row fields on a table, which are the ways out of it. */
export function linkRowFields(fields: readonly Field[]): Field[] {
  return fields.filter((field) => field.type === 'link_row')
}

/** The table a link row field points at, or null when it points nowhere. */
export function linkedTableId(field: Field | undefined): number | null {
  if (field === undefined || field.type !== 'link_row') return null
  return field.link_row_table_id ?? null
}

/**
 * The eleven aggregations a rollup may apply.
 *
 * Upstream filters its whole formula-function registry by
 * `isRollupCompatible(targetType)`, and every one of the eleven answers `true`
 * whatever it is handed -- so the argument decides nothing and the list is the
 * same for every target. Written out here rather than derived from a registry
 * this fork does not have.
 *
 * A combination that makes no sense -- summing a text column -- is not refused:
 * the backend accepts it and puts an error on the field, which is what the
 * dialog then shows.
 */
export const ROLLUP_FUNCTIONS = [
  'sum',
  'avg',
  'min',
  'max',
  'count',
  'any',
  'every',
  'stddev_pop',
  'stddev_sample',
  'variance_pop',
  'variance_sample',
] as const

export type RollupFunction = (typeof ROLLUP_FUNCTIONS)[number]

/**
 * The expression these fields are, in the formula language.
 *
 * Not a convenience: a rollup field's `save()` builds exactly this string and
 * stores it as the field's formula, and a lookup's does the same without the
 * function around it. So asking the typing endpoint about it is asking the
 * backend what the field being described *will be* -- which comes back with the
 * formatting that type carries, the same way the formula editor gets it.
 *
 * Null when there is not enough to ask about yet.
 */
export function relationExpression({
  through,
  target,
  rollupFunction,
}: {
  through: Field | undefined
  target: Field | undefined
  rollupFunction?: string
}): string | null {
  if (through === undefined || target === undefined) return null
  const lookup = `lookup(${nameLiteral(through.name)},${nameLiteral(target.name)})`
  if (rollupFunction === undefined) return lookup
  return rollupFunction === '' ? null : `${rollupFunction}(${lookup})`
}
